import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useState } from "react";
import type { UrlPatternField as UrlPatternFieldType } from "../../../shared/connectors/types";
import {
  domainToRegex,
  regexToDomain,
  urlPatternError,
} from "../../../shared/connectors/urlPatternConversion";
import { sharedMutators } from "../../../shared/mutators";
import { Box } from "../Box";
import { Input } from "../Input";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import type { App } from "./Form";

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 8px;
`;

const ModeSwitcher = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModeTab = styled(PressButton)`
  all: unset;
  cursor: pointer;
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.3;
  &[data-active] {
    opacity: 1;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const Dot = styled("span")`
  font-size: 10px;
  line-height: 12px;
  color: var(--text-primary);
  opacity: 0.3;
`;

type Props = {
  item: UrlPatternFieldType;
  app: App;
  disabled?: boolean;
};

export function UrlPatternField({ item, app, disabled }: Props) {
  const z = useZero();
  const currentRegex = app.urlPattern;
  const isMatchAll = !currentRegex || currentRegex === ".*";
  const parsedDomain = isMatchAll ? "" : regexToDomain(currentRegex);

  const [mode, setMode] = useState<"domain" | "regex">(
    parsedDomain === null ? "regex" : "domain"
  );
  const [inputKey, setInputKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const persistRegex = useCallback(
    (regex: string) => {
      const problem = urlPatternError(regex);
      setError(problem);
      if (problem) return;
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: app.id,
          name: app.name,
          identifier: app.identifier,
          urlPattern: regex,
          configuration: app.configuration,
        })
      );
    },
    [z, app]
  );

  const handleDomainChange = useCallback(
    (value: string) => {
      persistRegex(domainToRegex(value));
    },
    [persistRegex]
  );

  const isDomain = mode === "domain";
  const canSimplify = parsedDomain !== null;

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {isDomain ? "Allowed for URLs starting with" : "Allowed URL patterns"}
        </Text>
      </Box>
      <Input
        key={inputKey}
        type="text"
        defaultValue={isDomain ? (parsedDomain ?? "") : currentRegex}
        placeholder={isDomain ? item.placeholder : ".*"}
        onFinishChange={isDomain ? handleDomainChange : persistRegex}
        style={{ width: "100%" }}
        readOnly={disabled}
      />
      <HelperRow>
        <Text
          variant="text-small"
          color={error ? "var(--red-600)" : "var(--text-secondary)"}
        >
          {error ??
            (isDomain
              ? "Use * as a wildcard, e.g. *.example.com."
              : "URLs matching this pattern will use your credentials.")}
        </Text>
        {canSimplify && (
          <ModeSwitcher>
            <ModeTab
              data-active={isDomain || undefined}
              onPress={() => {
                if (!isDomain) {
                  setMode("domain");
                  setInputKey((k) => k + 1);
                  setError(null);
                }
              }}
            >
              Simple
            </ModeTab>
            <Dot>•</Dot>
            <ModeTab
              data-active={!isDomain || undefined}
              onPress={() => {
                if (isDomain) {
                  setMode("regex");
                  setInputKey((k) => k + 1);
                  setError(null);
                }
              }}
            >
              Regex
            </ModeTab>
          </ModeSwitcher>
        )}
      </HelperRow>
    </div>
  );
}
